import React from 'react';
import { useNavigate } from 'react-router-dom';

import './EventCard.css';

import { isAuthError } from '../../api/client';
import type { EventDto } from '../../api/types';
import {
  getEventActionConfirmCopy,
  type EventPendingActionKind,
} from '../../domain/events/actionConfirm';
import { findUserSlot } from '../../domain/events/participants';
import { canEditEvent, canReserveSlot } from '../../domain/events/policy';
import { AUTOMATIC_ORGANIZER_NAME, isAutomaticOrganizer } from '../../domain/matching/matching';
import { GameCard } from '../../features/games/GameCard';
import { ParticipantSelectModal } from '../../features/selection/ParticipantSelectModal';
import { useUsersById } from '../../hooks';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { selectGameById } from '../../store/selectors/gamesSelectors';
import { selectMatchingEventView } from '../../store/selectors/matchingSelectors';
import { uiActions } from '../../store/slices/uiSlice';
import {
  cancelEvent,
  confirmEvent,
  joinAndConfirmEvent,
  leaveEvent,
  removeFromEvent,
  unreserveFromEvent,
  inviteToEvent,
  inviteGroupToEvent,
} from '../../store/thunks';
import { fetchGameById } from '../../store/thunks/gamesThunks';
import {
  TavernCard,
  TavernCardHeader,
  TavernCardTitle,
  TavernCardMeta,
  TavernCardTime,
  OpenBadge,
  PrivateBadge,
  TavernIconButton,
  ExpandableText,
} from '../../ui/components';
import { ConfirmSheet } from '../../ui/ConfirmSheet';
import { AddFighterIcon } from '../../ui/icons/AddFighterIcon';
import { LinkIcon } from '../../ui/icons/LinkIcon';
import { copyToClipboard } from '../../utils/clipboard';
import { formatTimeRange, formatWeekdayDateShortRu } from '../../utils/time';
import { MembersList, RoleBadge } from '../members';

import { buildEventShareText } from './eventShareText';

import '../matching/matching.css';

type PendingAction =
  | null
  | { kind: 'join' }
  | { kind: 'leave' }
  | { kind: 'confirm' }
  | { kind: 'cancel' }
  | { kind: 'reserve' }
  | { kind: 'remove'; telegramId: number }
  | { kind: 'removeReservation'; telegramId: number };

type EventCardProps = {
  event: EventDto;
  membersOpen?: boolean;
  showDescription?: boolean;
};

function buildCurrentUserLabel(user: { first_name: string; last_name: string } | null): string {
  if (!user) return 'Я';
  const firstName = user.first_name || '';
  const lastName = user.last_name || '';
  const name = `${firstName} ${lastName}`.trim();
  return name ? `Я (${name})` : 'Я';
}

export const EventCard = React.memo(function EventCard({
  event,
  membersOpen,
  showDescription = true,
}: EventCardProps) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const currentUser = useAppSelector((s) => s.auth.user);
  const currentUserTelegramId = currentUser?.telegramId ?? '';
  const currentRole = currentUser?.role;

  // Collect all user IDs needed for this event (organizer + participants)
  const neededUserIds = React.useMemo(() => {
    const ids = event.participants.map((p) => p.telegramId);
    if (!ids.includes(event.createdByTelegramId)) {
      ids.push(event.createdByTelegramId);
    }
    return ids;
  }, [event.participants, event.createdByTelegramId]);

  const { getUser, isLoading: usersLoading } = useUsersById(neededUserIds);

  const fullGame = useAppSelector(selectGameById(event.game?.id ?? ''));

  React.useEffect(() => {
    if (event.game?.id && !fullGame) {
      dispatch(fetchGameById({ gameId: event.game.id }));
    }
  }, [dispatch, event.game?.id, fullGame]);

  const [confirm, setConfirm] = React.useState<PendingAction>(null);
  const [showInviteModal, setShowInviteModal] = React.useState(false);

  const currentUserLabel = buildCurrentUserLabel(
    currentUser ? { first_name: currentUser.first_name, last_name: currentUser.last_name } : null,
  );

  const organizerIsMe = event.createdByTelegramId === currentUserTelegramId;
  const organizerLabel = organizerIsMe
    ? currentUserLabel
    : `Организатор ${event.createdByTelegramId}`;

  const canEdit = canEditEvent({
    role: currentRole,
    event,
    currentUserTelegramId: currentUserTelegramId || null,
  });

  // While the roster is forming the system owns the event: people can neither edit nor invite.
  const matchingView = useAppSelector(selectMatchingEventView(event.id));
  const isForming = matchingView?.isForming === true;

  // Find current user's participation status
  const myParticipation = findUserSlot(event, currentUserTelegramId);
  const isParticipant = myParticipation !== null;
  const isPending = myParticipation?.status === 'pending';
  const isConfirmed = myParticipation?.status === 'confirmed';
  const isFull = event.participants.length >= event.maxParticipants;
  const canJoin = !isParticipant && event.visibility === 'open' && !isFull;
  const canReserve =
    isConfirmed &&
    !isFull &&
    canReserveSlot({
      role: currentRole,
      event,
      currentUserTelegramId: currentUserTelegramId || null,
    });

  const onConfirm = async () => {
    const action = confirm;
    setConfirm(null);

    if (!action) return;

    if (action.kind === 'join') {
      await dispatch(joinAndConfirmEvent({ eventId: event.id })).unwrap();
      return;
    }

    if (action.kind === 'reserve') {
      await dispatch(joinAndConfirmEvent({ eventId: event.id })).unwrap();
      return;
    }

    if (action.kind === 'leave') {
      await dispatch(leaveEvent({ eventId: event.id })).unwrap();
      return;
    }

    if (action.kind === 'cancel') {
      await dispatch(cancelEvent({ eventId: event.id })).unwrap();
      return;
    }

    if (action.kind === 'remove') {
      await dispatch(
        removeFromEvent({ eventId: event.id, telegramIds: [action.telegramId] }),
      ).unwrap();
      return;
    }

    if (action.kind === 'removeReservation') {
      await dispatch(
        unreserveFromEvent({ eventId: event.id, telegramIds: [action.telegramId] }),
      ).unwrap();
      return;
    }

    await dispatch(confirmEvent({ eventId: event.id })).unwrap();
  };

  const confirmCopy = React.useMemo(() => {
    if (!confirm) return null;
    if (confirm.kind === 'remove') {
      return {
        title: 'Удалить участника',
        message: 'Подтвердить удаление участника из мероприятия?',
        confirmLabel: 'Удалить',
        destructive: true,
      };
    }
    if (confirm.kind === 'removeReservation') {
      return {
        title: 'Убрать резервацию',
        message: 'Убрать зарезервированный слот? Основное участие сохранится.',
        confirmLabel: 'Убрать',
        destructive: true,
      };
    }
    if (confirm.kind === 'reserve') {
      return {
        title: 'Зарезервировать место',
        message: 'Зарезервировать место? Слот будет занят на ваше имя.',
        confirmLabel: 'Зарезервировать',
        destructive: false,
      };
    }
    return getEventActionConfirmCopy(confirm.kind as EventPendingActionKind);
  }, [confirm]);

  // Get user display name (auto-fetches users if not in store)
  const getUserName = (telegramId: string) => {
    if (isAutomaticOrganizer(telegramId)) {
      return (
        <span className="organizer-name organizer-name--system">{AUTOMATIC_ORGANIZER_NAME}</span>
      );
    }
    const user = getUser(telegramId);
    if (user) {
      const firstName = user.firstName || '';
      const lastName = user.lastName || '';
      const name = `${firstName} ${lastName}`.trim();
      const href = `https://t.me/${user.username}`.trim();
      const link = <a href={href}>{name}</a>;
      return link || `User ${telegramId}`;
    }
    return usersLoading ? 'Загрузка...' : `User ${telegramId}`;
  };

  const getUserAvatar = (telegramId: string) => {
    const user = getUser(telegramId);
    const photoUrl = user?.photoUrl;
    const isValidUrl = photoUrl && (photoUrl.startsWith('http') || photoUrl.startsWith('/'));
    if (isValidUrl) {
      return <img src={photoUrl} alt="" />;
    }
    const initial = user?.firstName?.[0]?.toUpperCase() || '?';
    return <span className="member-avatar-placeholder">{initial}</span>;
  };

  const getUserRole = (telegramId: string) => {
    const user = getUser(telegramId);
    if (!user) return null;
    return <RoleBadge role={user.role} hasClubKey={user.hasClubKey} />;
  };

  const handleInviteMembers = async (telegramIds: number[]) => {
    try {
      await dispatch(inviteToEvent({ eventId: event.id, telegramIds })).unwrap();
      setShowInviteModal(false);
    } catch (err: unknown) {
      if (!isAuthError(err)) {
        const message = err instanceof Error ? err.message : String(err);
        dispatch(
          uiActions.setNotification({ type: 'error', message: `Ошибка приглашения: ${message}` }),
        );
      }
    }
  };

  const handleInviteGroup = async (groupId: string) => {
    try {
      await dispatch(inviteGroupToEvent({ eventId: event.id, groupId })).unwrap();
      setShowInviteModal(false);
    } catch (err: unknown) {
      if (!isAuthError(err)) {
        const message = err instanceof Error ? err.message : String(err);
        dispatch(
          uiActions.setNotification({
            type: 'error',
            message: `Ошибка приглашения отряда: ${message}`,
          }),
        );
      }
    }
  };

  const excludeIds = event.participants.map((p) => Number(p.telegramId));

  // Open events can be shared by anyone; private ones only by people who are already in.
  const canShare = event.visibility === 'open' || canEdit || isParticipant;

  const handleCopyLink = async () => {
    const copied = await copyToClipboard(buildEventShareText(event, fullGame));
    dispatch(
      uiActions.setNotification(
        copied
          ? { type: 'success', message: 'Ссылка скопирована' }
          : { type: 'error', message: 'Не удалось скопировать ссылку' },
      ),
    );
  };

  const isPast = new Date(event.endAt) < new Date();
  const eventTimeRange = formatTimeRange(event.startAt, event.endAt);
  const eventDateLabel = formatWeekdayDateShortRu(event.startAt);

  return (
    <TavernCard variant="event" className={isPast ? 'event-card--past' : ''}>
      <TavernCardHeader>
        <div className="event-card-header-content">
          <TavernCardTitle>{event.title}</TavernCardTitle>
          <TavernCardMeta>
            <TavernCardTime timeRange={eventTimeRange} dateLabel={eventDateLabel} />
            {isForming ? <span className="matching-forming-badge">Состав формируется</span> : null}
            {(canEdit || canShare) && (
              <div className="event-card-actions">
                {canShare && (
                  <button
                    type="button"
                    className="event-copy-link-btn"
                    onClick={() => {
                      void handleCopyLink();
                    }}
                    aria-label="Скопировать ссылку"
                  >
                    <LinkIcon />
                  </button>
                )}
                {canEdit && !isForming && (
                  <>
                    <button
                      type="button"
                      className="event-invite-btn"
                      onClick={() => setShowInviteModal(true)}
                      aria-label="Пригласить участников"
                    >
                      <AddFighterIcon width="24" height="24" />
                    </button>
                    <button
                      type="button"
                      className="event-edit-btn"
                      onClick={() => navigate(`/events/${encodeURIComponent(event.id)}/edit`)}
                      aria-label="Редактировать"
                    >
                      ✎
                    </button>
                  </>
                )}
                {canEdit && (
                  <button
                    type="button"
                    className="event-delete-btn"
                    onClick={() => setConfirm({ kind: 'cancel' })}
                    aria-label="Удалить"
                  >
                    🗑
                  </button>
                )}
              </div>
            )}
          </TavernCardMeta>
        </div>
      </TavernCardHeader>

      <div className="event-card-bodywrap">
        <div className="event-card-body">
          <div className="event-card-col">
            {/* Organizer — compact single-line */}
            <div className="event-card-organizer">
              <div className="event-card-organizer-main">
                <span className="organizer-icon">👑</span>
                {organizerIsMe ? (
                  <span className="organizer-name">{currentUserLabel}</span>
                ) : (
                  getUserName(event.createdByTelegramId)
                )}
              </div>
              {event.visibility === 'open' ? <OpenBadge /> : <PrivateBadge />}
            </div>

            {showDescription && event.description?.trim() ? (
              <div className="event-card-description">
                <ExpandableText text={event.description} markdown />
              </div>
            ) : null}
          </div>

          <div className="event-card-col">
            {/* Game card — if a game is linked to this event */}
            {fullGame && (
              <div className="event-card-game">
                <GameCard game={fullGame} collapsibleTags />
              </div>
            )}

            {/* Participants list */}
            <MembersList
              members={event.participants}
              currentUserTelegramId={currentUserTelegramId}
              canEdit={canEdit && !isForming}
              renderName={getUserName}
              renderAvatar={getUserAvatar}
              renderRole={getUserRole}
              onConfirm={() => setConfirm({ kind: 'confirm' })}
              onLeave={() => setConfirm({ kind: 'leave' })}
              onRemove={(telegramId) => setConfirm({ kind: 'remove', telegramId })}
              onRemoveReservation={(telegramId) =>
                setConfirm({ kind: 'removeReservation', telegramId })
              }
              capacity={{
                maxCapacity: event.maxParticipants,
                emptySlotLabel: event.visibility === 'open' ? 'Свободно' : 'Закрыто',
              }}
              canJoin={canJoin}
              onJoin={() => setConfirm({ kind: 'join' })}
              canReserve={canReserve}
              onReserve={() => setConfirm({ kind: 'reserve' })}
              headerLabel="Участники"
              collapsible={true}
              defaultVisible={membersOpen}
            />
          </div>
        </div>
      </div>

      <ConfirmSheet
        open={confirm != null}
        title={confirmCopy?.title ?? ''}
        message={confirmCopy?.message ?? ''}
        confirmLabel={confirmCopy?.confirmLabel ?? 'Подтвердить'}
        destructive={confirmCopy?.destructive ?? false}
        onCancel={() => setConfirm(null)}
        onConfirm={() => {
          void onConfirm();
        }}
      />

      <ParticipantSelectModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        onSelectMember={handleInviteMembers}
        onSelectGroup={handleInviteGroup}
        excludeIds={excludeIds}
      />
    </TavernCard>
  );
});
